import React from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Modal,
  Paper,
  IconButton,
  styled
} from '@mui/material';
import { motion } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';

import THEME from '../../../constants/theme';
import merinLogo from '../../../assets/icons/merin-logo.svg'; // Added import
import dravonLogo from '../../../assets/icons/dravon-logo.svg'; // Added import

// Styled components for the modal
const StyledModal = styled(Modal)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const ModalContent = styled(Paper)(() => ({
  backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
  boxShadow: THEME.SHADOWS.LARGE,
  padding: THEME.SPACING.XL,
  outline: 'none',
  maxWidth: '800px',
  width: '80%',
  maxHeight: '90%',
  overflow: 'auto',
  position: 'relative',
  borderRadius: '8px'
}));

const CloseButton = styled(IconButton)(() => ({
  position: 'absolute',
  right: THEME.SPACING.MEDIUM,
  top: THEME.SPACING.MEDIUM,
  color: THEME.COLORS.NEUTRAL_GRAY,
  '&:hover': {
    color: THEME.COLORS.NEUTRAL_BLACK,
    backgroundColor: 'rgba(0,0,0,0.05)'
  }
}));

// New function to render title with logos
const renderTitleWithLogos = title => {
  if (!title) return title;

  const parts = [];
  let lastIndex = 0;
  // Regex to find the custom logo placeholders
  const regex = /(\{\{MERIN_LOGO\}\}|\{\{DRAVON_LOGO\}\})/g;
  let key = 0;
  let currentMatch;

  while ((currentMatch = regex.exec(title)) !== null) {
    // Add text before the marker
    if (currentMatch.index > lastIndex) {
      parts.push(title.substring(lastIndex, currentMatch.index));
    }

    // Add the logo based on the marker
    const marker = currentMatch[0];
    if (marker === '{{MERIN_LOGO}}') {
      parts.push(
        <img
          key={`title-logo-merin-${key++}`}
          src={merinLogo}
          alt='Merin Logo'
          style={{
            height: '1em',
            maxHeight: '24px',
            marginRight: '4px',
            verticalAlign: 'middle'
          }}
        />
      );
    } else if (marker === '{{DRAVON_LOGO}}') {
      parts.push(
        <img
          key={`title-logo-dravon-${key++}`}
          src={dravonLogo}
          alt='Dravon Logo'
          style={{
            height: '1em',
            maxHeight: '24px',
            marginRight: '4px',
            verticalAlign: 'middle'
          }}
        />
      );
    }
    lastIndex = regex.lastIndex;
  }

  // Add any remaining text after the last marker
  if (lastIndex < title.length) {
    parts.push(title.substring(lastIndex));
  }

  if (parts.length === 0) {
    return title;
  }
  // If only one part and it's the original title (no logos found), return the title directly.
  if (
    parts.length === 1 &&
    typeof parts[0] === 'string' &&
    parts[0] === title
  ) {
    return title;
  }

  // Wrap parts in React.Fragment with keys for proper rendering
  return parts.map((part, index) => (
    <React.Fragment key={`title-part-${index}`}>{part}</React.Fragment>
  ));
};

// Function to render news detail content with logos and bold text
const renderNewsDetailContent = content => {
  if (!content) return null;

  const elements = [];
  let lastIndex = 0;
  // Regex to find custom logo placeholders, **bolded text**, or URLs
  const regex =
    /(\{\{MERIN_LOGO\}\}|\{\{DRAVON_LOGO\}\}|\*\*([^*]+)\*\*|https?:\/\/[^\s]+)/g;
  let key = 0;
  let match;

  while ((match = regex.exec(content)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      elements.push(content.substring(lastIndex, match.index));
    }

    const matchedString = match[0];
    if (matchedString === '{{MERIN_LOGO}}') {
      elements.push(
        <img
          key={`content-logo-merin-${key++}`}
          src={merinLogo}
          alt='Merin Logo'
          style={{
            height: '1.2em',
            verticalAlign: 'bottom',
            marginRight: '0.1em',
            marginLeft: '0.1em'
          }}
        />
      );
    } else if (matchedString === '{{DRAVON_LOGO}}') {
      elements.push(
        <img
          key={`content-logo-dravon-${key++}`}
          src={dravonLogo}
          alt='Dravon Logo'
          style={{
            height: '1.2em',
            verticalAlign: 'bottom',
            marginRight: '0.1em',
            marginLeft: '0.1em'
          }}
        />
      );
    } else if (match[2]) {
      // Matched **bolded text** (match[2] is the content inside **)
      elements.push(<strong key={`content-bold-${key++}`}>{match[2]}</strong>);
    } else if (
      matchedString.startsWith('http://') ||
      matchedString.startsWith('https://')
    ) {
      // Matched a URL - render as clickable link
      elements.push(
        <a
          key={`content-link-${key++}`}
          href={matchedString}
          target='_blank'
          rel='noopener noreferrer'
          style={{
            color: THEME.COLORS.PRIMARY_BLUE,
            textDecoration: 'underline'
          }}
        >
          {matchedString}
        </a>
      );
    }
    lastIndex = regex.lastIndex;
  }

  // Add any remaining text after the last match
  if (lastIndex < content.length) {
    elements.push(content.substring(lastIndex));
  }

  return elements.map((part, index) => (
    <React.Fragment key={`content-part-${index}`}>{part}</React.Fragment>
  ));
};

/**
 * NewsCarousel component for displaying scrollable news items with modal functionality
 *
 * @param {Object} props - Component props
 * @param {Array} props.newsItems - Array of news objects with title, date, content
 * @param {Function} props.setNewsCount - Function to update visible news count
 * @returns {React.Component} NewsCarousel component
 */
const NewsCarousel = ({ newsItems = [], setNewsCount }) => {
  const [visibleNews, setVisibleNews] = useState(new Set([0, 1]));
  const containerRef = useRef(null);
  const newsRefs = useRef([]);

  // Modal state
  const [selectedNews, setSelectedNews] = useState(null);
  const [open, setOpen] = useState(false);

  const handleOpen = news => {
    setSelectedNews(news);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const visibleSet = new Set();

    newsRefs.current.forEach((ref, index) => {
      if (
        ref &&
        ref.getBoundingClientRect().top >=
          container.getBoundingClientRect().top &&
        ref.getBoundingClientRect().bottom <=
          container.getBoundingClientRect().bottom
      ) {
        visibleSet.add(index);
      }
    });

    setVisibleNews(visibleSet);
    setNewsCount && setNewsCount(visibleSet.size);
  }, [setNewsCount]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial call to set the visible news
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [newsItems, handleScroll]);
  if (!newsItems || newsItems.length === 0) {
    return (
      <Typography
        variant='body1'
        sx={{
          fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
          fontSize: '1.2rem',
          padding: '1rem 0'
        }}
      >
        No news currently available. Please check back later.
      </Typography>
    );
  }

  return (
    <React.Fragment>
      {/* News Count Display */}
      <Box mt={2}>
        <Typography
          variant='h6'
          sx={{
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
            fontSize: '1.2rem'
          }}
        >
          {`Viewing ${Math.min(visibleNews.size, newsItems.length)} of ${newsItems.length} News`}
        </Typography>
      </Box>

      {/* News List */}
      <Box
        ref={containerRef}
        sx={{
          height: '32vh', // Reduced height from 60vh to 50vh
          overflowY: 'auto',
          padding: '16px',
          mt: 2,
          borderRadius: '8px'
        }}
      >
        {newsItems.map((news, index) => (
          <motion.div
            key={index}
            data-index={index}
            ref={el => (newsRefs.current[index] = el)}
            initial={{ y: 40 }}
            animate={
              visibleNews.has(index)
                ? { y: 0, scale: 1 }
                : { y: 20, scale: 0.95 }
            }
            transition={{
              y: { duration: 0.6 },
              scale: { duration: 0.4 },
              delay: index * 0.1
            }}
            style={{
              marginBottom: '1rem', // Reduced from 1.5rem
              padding: '1rem', // Reduced from 1.5rem
              backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
              borderRadius: '8px',
              boxShadow: visibleNews.has(index)
                ? '0px 4px 10px rgba(0, 0, 0, 0.1)'
                : 'none',
              cursor: 'pointer',
              border: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`
            }}
            onClick={() => handleOpen(news)}
          >
            <Box sx={{ mb: 0.5 }}>
              {' '}
              {/* Reduced from mb: 1 */}
              <Typography
                variant='h6'
                sx={{
                  fontSize: '1.2rem', // Reduced from 1.3rem
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                  fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                  color: THEME.COLORS.PRIMARY_BLUE,
                  mb: 0.5 // Reduced from 1
                }}
              >
                {renderTitleWithLogos(news.TITLE || news.title)}
              </Typography>
              {(news.DATE || news.date) && (
                <Typography
                  variant='body2'
                  sx={{
                    fontSize: '0.85rem', // Reduced from 0.9rem
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                    color: THEME.COLORS.NEUTRAL_GRAY_DARK,
                    fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM
                  }}
                >
                  {news.DATE || news.date}
                </Typography>
              )}
              <Typography
                variant='body1'
                sx={{
                  fontSize: '0.95rem', // Reduced from 1rem
                  fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                  mt: 1, // Reduced from mt: 2
                  whiteSpace: 'pre-line' // Added to render newline characters
                }}
              >
                {/* Apply renderNewsDetailContent to the preview content as well */}
                {renderNewsDetailContent(
                  (news.CONTENT || news.content) &&
                    (news.CONTENT || news.content).length > 100
                    ? (news.CONTENT || news.content).slice(0, 100) + '...'
                    : news.CONTENT || news.content
                )}
              </Typography>
            </Box>
          </motion.div>
        ))}
      </Box>

      {/* Modal for displaying full news content */}
      {selectedNews && (
        <StyledModal
          open={open}
          onClose={handleClose}
          aria-labelledby='news-detail-title'
          aria-describedby='news-detail-description'
        >
          <ModalContent>
            <CloseButton onClick={handleClose}>
              <CloseIcon />
            </CloseButton>
            <Typography
              variant='h5'
              component='h2'
              id='news-detail-title'
              sx={{
                mb: 2,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                color: THEME.COLORS.PRIMARY_BLUE, // Ensure title color matches card
                display: 'flex', // Added for alignment with potential logos
                alignItems: 'center' // Added for alignment
              }}
            >
              {renderTitleWithLogos(selectedNews.TITLE || selectedNews.title)}
            </Typography>
            <Typography
              variant='body1'
              id='news-detail-description'
              component='div' // Use component="div" to allow complex children like images and strong tags
              sx={{
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                whiteSpace: 'pre-line', // Ensures \n are rendered as line breaks
                color: THEME.COLORS.NEUTRAL_BLACK,
                lineHeight: '1.6'
                // The comment here is fine, the previous error was likely due to the ternary operator.
              }}
            >
              {renderNewsDetailContent(
                selectedNews.CONTENT || selectedNews.content
              )}
            </Typography>
          </ModalContent>
        </StyledModal>
      )}
    </React.Fragment>
  );
};

export default NewsCarousel;
